"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ForumSessionUser, getForumUser, WRITES_ENABLED } from "./PublicAuth";
import { SIGNUP_URL } from "../_lib/api";

// Every "join" call to action on the public site, session-aware. Server
// render = the anonymous version (that's what crawlers and new visitors see);
// after mount a signed-in agent gets a post button instead of a pitch.
type Variant = "masthead" | "hero" | "card" | "inline";

const primary =
  "inline-flex items-center justify-center rounded-md bg-blue-600 font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50";

export function SessionCta({ variant, categorySlug }: { variant: Variant; categorySlug?: string }) {
  const [user, setUser] = useState<ForumSessionUser | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setUser(getForumUser());
    setMounted(true);
  }, []);
  const signedIn = !!user && WRITES_ENABLED;
  // Before hydration the guest CTA is hidden by CSS in browsers that hold a
  // session (see layout: data-forum-auth), and a neutral placeholder keeps the
  // layout from jumping. After mount React renders the real state.
  const guest = mounted ? "" : " fp-guest";
  const postHref = `/submit${categorySlug ? `?category=${encodeURIComponent(categorySlug)}` : ""}`;

  if (variant === "masthead") {
    return signedIn ? (
      <Link href={postHref} className={`${primary} h-9 px-3 text-[13px] sm:px-3.5`}>
        <span className="sm:hidden">Post</span>
        <span className="hidden sm:inline">New post</span>
      </Link>
    ) : (
      <>
        <a href={SIGNUP_URL} className={`${primary} h-9 px-3 text-[13px] sm:px-3.5${guest}`}>
          <span className="sm:hidden">Join</span>
          <span className="hidden sm:inline">Join free</span>
        </a>
        {!mounted && <span aria-hidden className="fp-member-ph inline-block h-9 w-[5.5rem] rounded-md bg-blue-600/15" />}
      </>
    );
  }

  if (variant === "hero") {
    return signedIn ? (
      <Link href={postHref} className={`${primary} h-10 px-4 text-[14px]`}>
        New post
      </Link>
    ) : (
      <>
        <a href={SIGNUP_URL} className={`${primary} h-10 px-4 text-[14px]${guest}`}>
          Join the floor
        </a>
        {!mounted && <span aria-hidden className="fp-member-ph inline-block h-10 w-24 rounded-md bg-blue-600/15" />}
      </>
    );
  }

  if (variant === "inline") {
    return signedIn ? (
      <Link href={postHref} className={`${primary} mt-4 h-9 px-4 text-[13px]`}>
        Post the first thread
      </Link>
    ) : (
      <a href={SIGNUP_URL} className={`${primary} mt-4 h-9 px-4 text-[13px]${guest}`}>
        Join and post the first thread
      </a>
    );
  }

  // card
  if (signedIn) {
    const first = (user!.name || "").split(" ")[0] || "there";
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="fp-serif text-[18px] font-medium leading-snug text-slate-900">You&apos;re on the floor, {first}.</p>
        <p className="mt-2 text-[13px] leading-5 text-slate-500">
          Post a case, a win or a question. Keep client identifiers out and it goes up right away.
        </p>
        <Link href={postHref} className={`${primary} mt-3 h-9 px-3.5 text-[13px]`}>
          New post
        </Link>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="fp-serif text-[18px] font-medium leading-snug text-slate-900">Reading is free. Posting takes an InsuraCentral account.</p>
      <p className="mt-2 text-[13px] leading-5 text-slate-500">
        The CRM and multi-line dialer built for life insurance agents. Agents sign in here with one click, no second account.
      </p>
      <a href={SIGNUP_URL} className={`${primary} mt-3 h-9 px-3.5 text-[13px]${guest}`}>
        Start free
      </a>
    </div>
  );
}
